import type { CSSProperties, ElementType, FC, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'

type RevealFrom = 'up' | 'down' | 'left' | 'right' | 'scale' | 'rise' | 'blur' | 'none'

interface RevealProps {
    children: ReactNode
    as?: ElementType
    className?: string
    style?: CSSProperties
    from?: RevealFrom
    distance?: number
    delay?: number
    duration?: number
    stagger?: number
    start?: string
    /* When set, direct children are staggered instead of the wrapper itself. */
    childSelector?: string
    id?: string
}

const offsets: Record<RevealFrom, gsap.TweenVars> = {
    up: { y: 40 },
    down: { y: -20 },
    left: { x: -100 },
    right: { x: 50 },
    scale: { scale: 1.3 },
    /* A slightly heavier arrival with a touch of rotation, for cards and
       panels where a straight fade reads as flat. */
    rise: { y: 64, rotate: 1.2, scale: 0.98 },
    blur: { y: 26, filter: 'blur(10px)' },
    none: {}
}

/* The reference animates almost every block in from a small offset as it
   enters, and the offsets are section-specific rather than one global fade.
   This wrapper keeps those per-section values explicit at the call site. */
export const Reveal: FC<RevealProps> = ({
    children,
    as: Tag = 'div',
    className,
    style,
    from = 'up',
    distance,
    delay = 0,
    duration = 1.1,
    stagger = 0.08,
    start = 'top 86%',
    childSelector,
    id
}): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root) {
            return
        }

        const targets = childSelector
            ? Array.from(root.querySelectorAll<HTMLElement>(childSelector))
            : [root]

        if (targets.length === 0) {
            return
        }

        if (prefersReducedMotion()) {
            gsap.set(targets, { opacity: 1, x: 0, y: 0, scale: 1, rotate: 0, filter: 'none' })

            return
        }

        const base = { ...offsets[from] }

        if (typeof distance === 'number') {
            if ('y' in base) {
                base.y = base.y! < 0 ? -distance : distance
            }

            if ('x' in base) {
                base.x = base.x! < 0 ? -distance : distance
            }
        }

        gsap.fromTo(targets,
            { opacity: 0, ...base },
            {
                opacity: 1,
                x: 0,
                y: 0,
                scale: 1,
                rotate: 0,
                ...(from === 'blur' ? { filter: 'blur(0px)' } : {}),
                /* The end state is the identity transform, so dropping the
                   inline style it was written into changes nothing visually and
                   hands the element back to CSS. Without this, a wrapper that
                   also carries a hover transform (`.lift`) would be overridden
                   by GSAP's leftover inline transform and never move. A
                   standing `filter` is cleared for the same reason, and because
                   it forces a compositing layer that softens text. */
                clearProps: from === 'blur' ? 'transform,filter' : 'transform',
                duration,
                delay,
                stagger,
                ease: 'noma',
                scrollTrigger: {
                    trigger: root,
                    start,
                    once: true
                }
            }
        )
    }, scope, [from, childSelector])

    return (
        <Tag id={id} ref={scope} className={cn(className)} style={style}>
            {children}
        </Tag>
    )
}
